import { useEffect, useMemo, useState } from "react";
import { readEventSlugFromUrl, readSauceFromUrl, submitFeedback, submitSession } from "./lib/api";
import {
  getOrCreateDeviceId,
  getRatedSauces,
  hasScanned,
  hasSeenWelcome,
  hasSubmittedBrand,
  hasSubmittedSession,
  loadContact,
  markBrandSubmitted,
  markSessionSubmitted,
  markWelcomeSeen,
  recordScan,
  saveContact,
} from "./lib/device";
import { type CachedContact, SAUCE_TYPES, type SauceType, type SurveyAnswers } from "./lib/types";
import { AlreadyRatedScreen } from "./screens/AlreadyRatedScreen";
import { ErrorScreen } from "./screens/ErrorScreen";
import { RateScreen } from "./screens/RateScreen";
import { SauceSelectScreen } from "./screens/SauceSelectScreen";
import { ThanksScreen } from "./screens/ThanksScreen";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import { WrapUpScreen } from "./screens/WrapUpScreen";

type Screen = "welcome" | "select" | "rate" | "thanks" | "wrap" | "already" | "error";

interface ContactPayload {
  contactName: string | null;
  email: string | null;
  phone: string | null;
  zip: string | null;
  marketingOptIn: boolean;
}

const DEFAULT_EVENT_SLUG =
  (import.meta.env.VITE_DEFAULT_EVENT_SLUG as string | undefined) ?? "current-event";

export function App() {
  const eventSlug = useMemo(() => readEventSlugFromUrl(DEFAULT_EVENT_SLUG), []);
  const deviceUuid = useMemo(() => getOrCreateDeviceId(), []);
  const urlSauce = useMemo(() => readSauceFromUrl(), []);

  const [ratedSauces, setRatedSauces] = useState<Set<SauceType>>(() => getRatedSauces(eventSlug));
  const [cachedContact, setCachedContact] = useState<CachedContact | null>(() => loadContact());
  const [submitting, setSubmitting] = useState(false);

  const initialScreen = (): { screen: Screen; sauce: SauceType | null } => {
    // Welcome shows once per event. Cached contact is pre-filled into the
    // form so repeat customers just confirm and Continue — but we still want
    // them to re-acknowledge consent and create a session row for this event.
    const welcomeNeeded = !hasSeenWelcome(eventSlug);

    if (urlSauce) {
      if (hasScanned(eventSlug, urlSauce)) return { screen: "already", sauce: urlSauce };
      if (welcomeNeeded) return { screen: "welcome", sauce: urlSauce };
      return { screen: "rate", sauce: urlSauce };
    }

    if (welcomeNeeded) return { screen: "welcome", sauce: null };

    // Returning user who finished all 4 but never gave brand vibe lands on wrap.
    if (ratedSauces.size >= SAUCE_TYPES.length && !hasSubmittedBrand(eventSlug)) {
      return { screen: "wrap", sauce: null };
    }

    return { screen: "select", sauce: null };
  };

  const [{ screen, sauce }, setScreenState] = useState<{
    screen: Screen;
    sauce: SauceType | null;
  }>(initialScreen);
  const [scannedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    document.documentElement.dataset.event = eventSlug;
  }, [eventSlug]);

  const goToSelect = () => {
    setScreenState({ screen: "select", sauce: null });
  };

  const pickSauce = (s: SauceType) => {
    if (hasScanned(eventSlug, s)) {
      setScreenState({ screen: "already", sauce: s });
      return;
    }
    setScreenState({ screen: "rate", sauce: s });
  };

  const handleWelcomeJoin = async (payload: ContactPayload, display: CachedContact) => {
    setSubmitting(true);
    try {
      await submitSession({
        eventSlug,
        deviceUuid,
        contactName: payload.contactName,
        email: payload.email,
        phone: payload.phone,
        zip: payload.zip,
        marketingOptIn: payload.marketingOptIn,
      });
      saveContact(display);
      setCachedContact(display);
      markWelcomeSeen(eventSlug);
      markSessionSubmitted(eventSlug);
      setScreenState(
        urlSauce && !hasScanned(eventSlug, urlSauce)
          ? { screen: "rate", sauce: urlSauce }
          : { screen: "select", sauce: null },
      );
    } catch (err) {
      console.error(err);
      setScreenState({ screen: "error", sauce: null });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWelcomeSkip = () => {
    markWelcomeSeen(eventSlug);
    setScreenState(
      urlSauce && !hasScanned(eventSlug, urlSauce)
        ? { screen: "rate", sauce: urlSauce }
        : { screen: "select", sauce: null },
    );
  };

  const handleRateNext = async (answers: SurveyAnswers) => {
    setSubmitting(true);
    try {
      await submitFeedback({
        eventSlug,
        deviceUuid,
        scannedAt,
        sauceType: answers.sauceType,
        sauceRating: answers.sauceRating,
        likedMost: answers.likedMost,
        dailyUse: answers.dailyUse,
        dailyUseComment: answers.dailyUseComment,
        nps: answers.nps,
        marketingOptIn: cachedContact?.marketingOptIn ?? false,
        contactName: cachedContact?.name?.trim() || null,
        email: cachedContact?.email?.trim().toLowerCase() || null,
        phone: phoneToE164(cachedContact?.phone),
        zip: cachedContact?.zip?.trim() || null,
      });
      recordScan(eventSlug, answers.sauceType, answers.sauceRating);
      const next = getRatedSauces(eventSlug);
      setRatedSauces(next);

      const allDone = next.size >= SAUCE_TYPES.length;
      if (allDone && !hasSubmittedBrand(eventSlug)) {
        setScreenState({ screen: "wrap", sauce: answers.sauceType });
      } else {
        setScreenState({ screen: "select", sauce: null });
      }
    } catch (err) {
      console.error(err);
      setScreenState({ screen: "error", sauce: answers.sauceType });
    } finally {
      setSubmitting(false);
    }
  };

  const handleWrapSubmit = async (data: {
    brandWords: string;
    contact: ContactPayload | null;
    display: CachedContact | null;
  }) => {
    setSubmitting(true);
    try {
      await submitSession({
        eventSlug,
        deviceUuid,
        brandWords: data.brandWords,
        completed: true,
        ...(data.contact
          ? {
              contactName: data.contact.contactName,
              email: data.contact.email,
              phone: data.contact.phone,
              zip: data.contact.zip,
              marketingOptIn: data.contact.marketingOptIn,
            }
          : {}),
      });
      if (data.display) {
        saveContact(data.display);
        setCachedContact(data.display);
      }
      markBrandSubmitted(eventSlug);
      markSessionSubmitted(eventSlug);
      setScreenState({ screen: "thanks", sauce: null });
    } catch (err) {
      console.error(err);
      setScreenState({ screen: "error", sauce: null });
    } finally {
      setSubmitting(false);
    }
  };

  switch (screen) {
    case "welcome":
      return (
        <WelcomeScreen
          initial={cachedContact}
          submitting={submitting}
          onJoin={(payload, display) => void handleWelcomeJoin(payload, display)}
          onSkip={handleWelcomeSkip}
        />
      );
    case "select":
      return <SauceSelectScreen ratedSauces={ratedSauces} onPick={pickSauce} />;
    case "already":
      if (!sauce) return <SauceSelectScreen ratedSauces={ratedSauces} onPick={pickSauce} />;
      return (
        <AlreadyRatedScreen sauce={sauce} ratedSauces={ratedSauces} onPickAnother={goToSelect} />
      );
    case "rate":
      if (!sauce) return <SauceSelectScreen ratedSauces={ratedSauces} onPick={pickSauce} />;
      return (
        <RateScreen
          sauceType={sauce}
          onBack={goToSelect}
          onNext={(answers) => void handleRateNext(answers)}
        />
      );
    case "thanks":
      return <ThanksScreen />;
    case "wrap":
      return (
        <WrapUpScreen
          initialContact={cachedContact}
          showContact={!hasSubmittedSession(eventSlug)}
          submitting={submitting}
          onSubmit={(data) => void handleWrapSubmit(data)}
        />
      );
    case "error":
      return (
        <ErrorScreen
          onRetry={() =>
            setScreenState(sauce ? { screen: "rate", sauce } : { screen: "select", sauce: null })
          }
        />
      );
  }
}

function phoneToE164(formatted: string | undefined): string | null {
  if (!formatted) return null;
  const d = formatted.replace(/\D+/g, "");
  return d.length === 10 ? `+1${d}` : null;
}
